import store from '../store/configureStore';

export const ScreentPermissionSet = Object.freeze({
    AGENCY_HIRE: 'agency_hire',
    RIDER_MANAGEMENT: 'rider_management',
    OPS_MANAGEMENT: 'ops_management',
    VENDOR_MANAGEMENT: 'vendor_management',
    CLUSTER_MANAGEMENT: 'cluster_management',
    EV_SP: 'ev_sp',
    EV_CM: 'ev-cm',
    STAFF: 'staff',
    ATTENDANCE: 'attendance',
    MAP_VIEW: 'map_view',
    REPORT: 'report',
    PAY_ONLINE: 'pay_online',
    CONTACTS: 'contacts',
    SETTINGS: 'settings',
    EV: 'ev',
    CONTROL_CENTER: 'control_center',
    QC_LEADS: 'qc_leads',
    HIRE: 'hire',
    ACCESS_MANAGEMENT: 'access_management',
    // Appended deliberately: getActiveInitRoute() returns the FIRST entry with read, so adding
    // any of these earlier would change the post-login landing route for existing users.
    BGV_TRACKING: 'bgv_tracking',
    BGV_BACKFILLING: 'bgv_backfilling',
    // Appended deliberately: getActiveInitRoute() returns the FIRST entry with read, so adding
    // this anywhere earlier would change the post-login landing route for existing users.
    USER_MANAGEMENT: 'user_management',
    APPROVAL_WORKFLOW: 'approval_workflow',
});

export const ScreenActionSet = Object.freeze({
    READ: 'read',
    UPDATE: 'update',
    CREATE: 'create',
    DELETE: 'delete',
    AUTH: 'auth',
    ALLOW_STAFF_DELETE: 'allowStaffDelete',
});

export const getPermissions = (screen) => {
    return {
        auth: canAllow(screen, ScreenActionSet.AUTH),
        read: canAllow(screen, ScreenActionSet.READ),
        update: canAllow(screen, ScreenActionSet.UPDATE),
        create: canAllow(screen, ScreenActionSet.CREATE),
        delete: canAllow(screen, ScreenActionSet.DELETE),
        allowStaffDelete: canAllow(screen, ScreenActionSet.ALLOW_STAFF_DELETE),
    };
};

export const canAllow = (screen, action) => {
    const configs = store.getState().user.config;
    const permission = configs.permission ?? {};
    const pScreen =
        ScreentPermissionSet[screen?.replace(/[-\s]/g, '_')?.toUpperCase()];

    return (
        permission.hasOwnProperty(pScreen) &&
        permission[pScreen]?.includes(action)
    );
};

export const SHIFT_ASSIGN_STAFF_KEY = 'shift_assign_staff';

/**
 * Resolves whether the current user can assign staff to a shift/hub.
 * When the `shift_assign_staff` permission is configured in
 * `user.config.permission`, it takes precedence and the user is allowed if it
 * grants create or update. Otherwise falls back to the permission passed via
 * props (existing behaviour).
 */
export const canAssignStaff = (fallbackPermissions) => {
    const permission = store.getState()?.user?.config?.permission ?? {};

    if (permission.hasOwnProperty(SHIFT_ASSIGN_STAFF_KEY)) {
        const actions = permission[SHIFT_ASSIGN_STAFF_KEY] ?? [];
        return (
            actions.includes(ScreenActionSet.CREATE) ||
            actions.includes(ScreenActionSet.UPDATE)
        );
    }

    return !!fallbackPermissions?.update;
};

// Slot Capacity Approval — the "Capacity Approvals" tab is a sub-tab INSIDE the existing
// /settings page, not its own route. It must NOT be added to ScreentPermissionSet: every entry
// there is scanned by getActiveInitRoute() (the post-login redirect target) and by the Navbar's
// nav-item generation as if it were a real top-level route — adding it there sent SFM-role users
// whose only granted screen permission was this one straight to a nonexistent `/slot-capacity...`
// route, landing on /404. Mirrors the existing SHIFT_ASSIGN_STAFF_KEY pattern: a standalone key,
// checked directly against user.config.permission, kept out of ScreentPermissionSet on purpose.
export const SLOT_CAPACITY_APPROVALS_KEY = 'slot_capacity_approvals';

export const getSlotCapacityApprovalsPermission = () => {
    const permission = store.getState()?.user?.config?.permission ?? {};
    const actions = permission[SLOT_CAPACITY_APPROVALS_KEY] ?? [];
    return {
        read: actions.includes(ScreenActionSet.READ),
        update: actions.includes(ScreenActionSet.UPDATE),
        create: actions.includes(ScreenActionSet.CREATE),
        delete: actions.includes(ScreenActionSet.DELETE),
    };
};

// Blue Dart RBAC — "hub_master" gates Add Hub, Bulk Upload Hubs and Bulk Map Vendors to Hubs,
// which are gated on ops_management.update today. RB-8 gives a Rider Approver shift create/edit
// but NOT those three, and one `update` bit cannot express that, so they move to their own key.
// Kept OUT of ScreentPermissionSet on purpose, exactly like SLOT_CAPACITY_APPROVALS_KEY above:
// every entry there is treated as a real top-level route by getActiveInitRoute(), and a user whose
// only granted screen was this one would be redirected to a nonexistent /hub-master.
export const HUB_MASTER_KEY = 'hub_master';

export const getHubMasterPermission = () => {
    const permission = store.getState()?.user?.config?.permission ?? {};
    const actions = permission[HUB_MASTER_KEY] ?? [];
    return {
        read: actions.includes(ScreenActionSet.READ),
        update: actions.includes(ScreenActionSet.UPDATE),
        create: actions.includes(ScreenActionSet.CREATE),
        delete: actions.includes(ScreenActionSet.DELETE),
    };
};

export const getActiveInitRoute = () => {
    let routes = Object.values(ScreentPermissionSet);
    let activeRoute = '';
    for (let item of routes) {
        const permission = getPermissions(item);
        if (permission.read) {
            activeRoute = item.replace('_', '-');
            break;
        }
    }
    return activeRoute ?? '404';
};
